import styled from 'styled-components';
import theme from '@raptormaps/theme';
import { FarmInfoCard } from './FarmInfoCard';
import { CardBody } from './FarmInfoCard.styles';

// ── Wrapper / page header ────────────────────────────────────────────

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 0 0.25rem;
`;

export const PageHeaderRight = styled.div`
  display: flex;
  gap: 0.625rem;
  flex-shrink: 0;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 3rem;
`;

// ── Badge ────────────────────────────────────────────────────────────

export const Badge = styled.span<{ $variant: 'green' | 'blue' | 'gray' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  ${({ $variant }) =>
    $variant === 'green' &&
    `background: ${theme.colors.green_100}; color: ${theme.colors.green_800};`}
  ${({ $variant }) =>
    $variant === 'blue' &&
    `background: ${theme.colors.info_100}; color: ${theme.colors.blue_600};`}
  ${({ $variant }) =>
    $variant === 'gray' &&
    `background: ${theme.colors.slate_200}; color: ${theme.colors.slate_600};`}
`;

// ── Grid / rows ──────────────────────────────────────────────────────

export const FarmGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const CardRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: stretch;
`;

export const ThreeRow = styled(CardRow)`
  & > * {
    flex: 1;
    min-width: 0;
  }
`;

export const LocationRow = styled(CardRow)`
  & > :first-child {
    flex: 2;
    min-width: 0;
  }
  & > :last-child {
    flex: 1;
    min-width: 0;
  }
`;

// ── Card variants ────────────────────────────────────────────────────

export const IdentificationCard = styled(FarmInfoCard)`
  flex: 1.6;
`;

export const CapacityCard = styled.div`
  flex: 1;
  background: ${theme.colors.slate_50};
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
`;

export const LocationCard = styled(FarmInfoCard)`
  ${CardBody} {
    padding: 0;
  }
`;

// ── Identification card ──────────────────────────────────────────────

export const IdTop = styled.div`
  display: flex;
  gap: 2.5rem;
  margin-bottom: 1rem;
`;

export const UuidBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: ${theme.colors.slate_50};
  border: 1px solid ${theme.colors.slate_200};
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
`;

export const UuidText = styled.span`
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  font-size: 0.8rem;
  color: ${theme.colors.blue_600};
  word-break: break-all;
`;

export const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.slate_500};
  padding: 0.125rem;
  flex-shrink: 0;
  border-radius: 4px;
  transition: color 0.15s;

  &:hover {
    color: ${theme.colors.blue_600};
  }
`;

export const CopiedText = styled.span`
  font-size: 0.7rem;
  color: ${theme.colors.green_500};
  font-weight: 600;
`;

// ── Capacity card ────────────────────────────────────────────────────

export const CapacityValue = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

export const CapacityNumber = styled.span`
  font-size: 3.5rem;
  font-weight: 800;
  color: ${theme.colors.grey_900};
  line-height: 1;
`;

export const CapacityUnit = styled.span`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${theme.colors.blue_600};
`;

export const Efficiency = styled.div`
  margin-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 1rem;
`;

export const EfficiencyRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

export const EfficiencyLabel = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.blue_300};
  opacity: 0.9;
`;

export const EfficiencyPct = styled.span`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${theme.colors.white};
`;

export const ProgressTrack = styled.div`
  height: 5px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 99px;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: ${theme.colors.blue_600};
  border-radius: 99px;
  transition: width 0.6s ease;
`;

// ── Location card ────────────────────────────────────────────────────

export const LocationInner = styled.div`
  display: flex;
`;

export const LocationFields = styled.div`
  flex: 1;
  padding: 1rem 1.25rem;
  border-right: 1px solid ${theme.colors.slate_200};
`;

export const LocationFieldsRow = styled.div`
  display: flex;
  gap: 2.5rem;
`;

export const CoordsTable = styled.table`
  margin-top: 0.875rem;
  border-collapse: collapse;
  width: 100%;
  max-width: 280px;
  border: 1px solid ${theme.colors.slate_200};
  border-radius: 6px;
  overflow: hidden;
`;

export const CoordsKey = styled.td`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${theme.colors.slate_500};
  padding: 0.4rem 0.75rem;
  background: ${theme.colors.slate_50};
  border-bottom: 1px solid ${theme.colors.slate_200};
  width: 90px;

  tr:last-child & {
    border-bottom: none;
  }
`;

export const CoordsVal = styled.td`
  font-size: 0.8rem;
  font-family: 'SF Mono', 'Fira Code', monospace;
  color: ${theme.colors.slate_600};
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid ${theme.colors.slate_200};

  tr:last-child & {
    border-bottom: none;
  }
`;

export const MapPlaceholder = styled.div`
  flex: 1;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.slate_50};
`;

export const MapNoCoords = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 180px;
`;

export const MapLink = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: ${theme.colors.blue_600};
  font-size: 0.875rem;
  font-weight: 500;
  padding: 1.5rem;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.8;
    text-decoration: none;
    color: ${theme.colors.blue_600};
  }
`;

export const MapLinkSub = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.slate_500};
`;

// ── Organization card ────────────────────────────────────────────────

export const OrgField = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid ${theme.colors.slate_200};

  &:last-child {
    border-bottom: none;
  }
`;

// ── Site Specifics card ──────────────────────────────────────────────

export const SpecValue = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.2rem;
  margin-top: 0.1rem;
`;

export const SpecBig = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${theme.colors.grey_900};
`;

export const SpecUnit = styled.span`
  font-size: 0.8rem;
  color: ${theme.colors.slate_500};
`;

// ── System History timeline ──────────────────────────────────────────

export const Timeline = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TimelineItem = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 0.625rem 0;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 5px;
    top: calc(0.625rem + 12px);
    bottom: 0;
    width: 2px;
    background: ${theme.colors.slate_200};
  }
`;

export const TimelineDot = styled.div<{ $color: 'blue' | 'green' | 'gray' }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 3px;
  position: relative;
  z-index: 1;
  background: ${({ $color }) => {
    if ($color === 'blue') return '${theme.colors.blue_600}';
    if ($color === 'green') return '${theme.colors.green_500}';
    return '${theme.colors.slate_500}';
  }};
`;

export const TimelineContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

export const TimelineDate = styled.div`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${theme.colors.grey_900};
`;

export const TimelineNote = styled.div`
  font-size: 0.72rem;
  color: ${theme.colors.slate_500};
`;

// ── Module Configuration card ────────────────────────────────────────

export const ModulesRow = styled.div`
  display: flex;
  gap: 1rem;
`;

export const ModuleTile = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex: 1;
  background: ${theme.colors.slate_50};
  border: 1px solid ${theme.colors.slate_200};
  border-radius: 8px;
  padding: 0.875rem 1rem;
`;

export const ModuleIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${theme.colors.info_100};
  border-radius: 8px;
  color: ${theme.colors.blue_600};
  flex-shrink: 0;
`;

export const ModuleVal = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${theme.colors.grey_900};
  margin-top: 0.15rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;
